import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Shield } from "lucide-react";

const SecureAdminSetup = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [adminExists, setAdminExists] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminExists();
  }, []);

  const checkAdminExists = async () => {
    try {
      // Check user_roles table instead of profiles
      const { data } = await supabase
        .from('user_roles')
        .select('id')
        .eq('role', 'admin')
        .single();

      if (data) {
        setAdminExists(true);
      }
    } catch (error) {
      setAdminExists(false);
    }
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      toast({
        variant: "destructive",
        title: "Erreur de validation",
        description: "Tous les champs sont requis",
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        variant: "destructive",
        title: "Erreur de validation",
        description: "Les mots de passe ne correspondent pas",
      });
      return false;
    }

    if (formData.password.length < 8) {
      toast({
        variant: "destructive",
        title: "Erreur de validation",
        description: "Le mot de passe doit contenir au moins 8 caractères",
      });
      return false;
    }

    return true;
  };

  const createAdminAccount = async () => {
    if (!validateForm()) return;

    setIsCreating(true);
    
    try {
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/admin-dashboard`,
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName
          }
        }
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        // Use the setup_initial_admin function to handle admin creation
        const { data: setupResult, error: setupError } = await supabase.rpc(
          'setup_initial_admin' as any,
          {
            admin_user_id: authData.user.id,
            admin_first_name: formData.firstName,
            admin_last_name: formData.lastName
          }
        );

        if (setupError) throw setupError;
        if (setupResult && typeof setupResult === 'object' && 'error' in setupResult) {
          throw new Error(setupResult.error as string);
        }

        toast({
          title: "Compte administrateur créé !",
          description: "Le compte admin a été configuré avec succès.",
        });

        setAdminExists(true);
        await supabase.auth.signOut();
        navigate('/auth');
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de créer le compte administrateur",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <Card className="max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Configuration Administrateur Sécurisée</CardTitle>
          <CardDescription>
            Créer un compte administrateur avec des identifiants personnalisés
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {adminExists ? (
            <div className="text-center space-y-4">
              <div className="p-4 bg-green-50 text-green-800 rounded-lg">
                <p className="font-medium">✅ Compte administrateur configuré</p>
                <p className="text-sm mt-1">Un administrateur existe déjà dans le système.</p>
              </div>
              <Button onClick={() => navigate('/auth')} className="w-full">
                Aller à la connexion
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Prénom</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  placeholder="Prénom de l'administrateur"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="lastName">Nom</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  placeholder="Nom de l'administrateur"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="admin@votreentreprise.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Minimum 8 caractères"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="Confirmer le mot de passe"
                />
              </div>
              
              <Button 
                onClick={createAdminAccount} 
                disabled={isCreating}
                className="w-full"
              >
                {isCreating ? "Création en cours..." : "Créer le compte administrateur"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SecureAdminSetup;